import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Helmet, HelmetTags } from 'react-helmet-async';
import { selectCurrentIDToken } from 'src/store/slices/auth/authSelectors';
import { selectConfigExpressChatbot } from 'src/store/slices/config/configSelectors';

const BOTPRESS_INJECT_SCRIPT_ID = 'botpress-inject';
const BOTPRESS_CLIENT_SCRIPT_ID = 'botpress-client';
const BOTPRESS_INJECT_SRC = 'https://cdn.botpress.cloud/webchat/v5.0/inject.js';
const BOTPRESS_EMBEDDED_CHAT_ID = 'bp-embedded-webchat';
const BOTPRESS_CLOSE_BUTTON_SELECTOR = '[aria-label="Close Chatbot Button"]';
// Mirrors the bot's configured theme color (customer-config express.chatbot).
const BOTPRESS_THEME_COLOR = '#3777a6';

interface BotpressWebchat {
  close: () => void;
  on: (event: string, callback: () => void) => void;
  open: () => void;
}

declare global {
  interface Window {
    botpress?: BotpressWebchat;
  }
}

export const ChatbotWidget = () => {
  const chatbot = useSelector(selectConfigExpressChatbot);
  const idToken = useSelector(selectCurrentIDToken);

  const isLoggedIn = !!idToken;
  const clientReference = chatbot?.client_reference;
  const isBotpressEnabled =
    chatbot?.provider === 'botpress' && !!clientReference;

  const [isInjectScriptLoaded, setIsInjectScriptLoaded] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Reset once per config change so a place switch (or config reload) waits
  // for its own inject.js to load before requesting the client script again.
  useEffect(() => {
    setIsInjectScriptLoaded(false);
  }, [clientReference]);

  // Botpress's Embedded-mode header renders its own "X" icon, but its
  // onClick handler is left unwired (verified via breakpoint — the click
  // reaches `stopPropagation()` then calls an undefined callback). That
  // stopPropagation() also blocks any *bubble*-phase listener on an
  // ancestor (document included) from ever seeing the click. Registering in
  // the capture phase instead fires before the event reaches the target —
  // and before any of Botpress's own bubble-phase code runs — so it's
  // unaffected by their stopPropagation() regardless of where their React
  // root actually mounts or whether it's behind a shadow boundary.
  useEffect(() => {
    if (!isBotpressEnabled || !isLoggedIn || !isInjectScriptLoaded) {
      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      // composedPath() includes every node the (composed) click event
      // passed through, shadow boundaries included — event.target would be
      // retargeted to the shadow host and miss the icon entirely.
      const isCloseButtonClick = event
        .composedPath()
        .some(
          (node) =>
            node instanceof Element &&
            node.matches(BOTPRESS_CLOSE_BUTTON_SELECTOR)
        );
      if (isCloseButtonClick) {
        window.botpress?.close();
        setIsPanelOpen(false);
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () =>
      document.removeEventListener('click', handleClick, { capture: true });
  }, [isBotpressEnabled, isLoggedIn, isInjectScriptLoaded]);

  // Keep local state in sync in case Botpress's own open/closed state ever
  // changes some other way (e.g. a future SDK update wires the button up).
  useEffect(() => {
    if (!isBotpressEnabled || !isLoggedIn || !isInjectScriptLoaded) {
      return undefined;
    }
    window.botpress?.on('webchat:opened', () => setIsPanelOpen(true));
    window.botpress?.on('webchat:closed', () => setIsPanelOpen(false));
    // window.botpress exposes no `off` method, so these listeners can't be
    // torn down individually — harmless, since window.botpress is a
    // page-lifetime singleton and this effect only fires once per config.
    return undefined;
  }, [isBotpressEnabled, isLoggedIn, isInjectScriptLoaded]);

  const handleReopen = useCallback(() => {
    window.botpress?.open();
    setIsPanelOpen(true);
  }, []);

  // inject.js sets up `window.botpress`; the client script calls into it as
  // soon as it runs. Dynamically created <script> tags default to async, so
  // rendering both up front races the two — only add the client script tag
  // once inject.js's onload has fired, guaranteeing `window.botpress` exists.
  const helmetChange = useCallback((_: unknown, addedTags: HelmetTags) => {
    const injectScriptTag = addedTags?.scriptTags?.find(
      (tag) => tag.id === BOTPRESS_INJECT_SCRIPT_ID
    );
    if (injectScriptTag) {
      injectScriptTag.onload = () => setIsInjectScriptLoaded(true);
    }
  }, []);

  if (!isBotpressEnabled || !isLoggedIn) {
    return null;
  }

  const scripts: { id: string; src: string; defer?: string }[] = [
    { id: BOTPRESS_INJECT_SCRIPT_ID, src: BOTPRESS_INJECT_SRC },
  ];
  if (isInjectScriptLoaded) {
    scripts.push({
      id: BOTPRESS_CLIENT_SCRIPT_ID,
      src: clientReference,
      defer: 'true',
    });
  }

  return (
    <>
      <Helmet
        script={scripts}
        onChangeClientState={helmetChange}
      />
      <div
        id={BOTPRESS_EMBEDDED_CHAT_ID}
        style={{
          bottom: 0,
          display: isPanelOpen ? 'block' : 'none',
          height: '640px',
          maxHeight: '80vh',
          maxWidth: '90vw',
          position: 'fixed',
          right: 0,
          width: '380px',
          zIndex: 1000,
        }}
      />
      {!isPanelOpen && (
        <button
          aria-label="Open Chatbot"
          onClick={handleReopen}
          style={{
            alignItems: 'center',
            backgroundColor: BOTPRESS_THEME_COLOR,
            border: 'none',
            borderRadius: '50%',
            bottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            height: '56px',
            justifyContent: 'center',
            position: 'fixed',
            right: '20px',
            width: '56px',
            zIndex: 1000,
          }}
          type="button"
        >
          <svg
            aria-hidden="true"
            fill="none"
            height="26"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            viewBox="0 0 24 24"
            width="26"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}
    </>
  );
};
